import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────────────
// Stock-source config — a declarative description of how to crawl a site's full
// vehicle inventory over plain HTTP and sync it into the `stock_items` table.
// Interpreted by the backend `StockScrapeEngine`. Standalone subsystem: NOT the
// tools scraper config (scraper-config.schema.ts), which runs live per tool
// call — this one has no tool args, and adds field_map (parsed field →
// canonical stock_items column), per-page politeness delay, and auth-expiry
// detection for session-gated portals.
// ─────────────────────────────────────────────────────────────────────────────

export const stockSourceRequestSchema = z.object({
  base_url: z.string().url(),
  // Optional URL fetched ONCE before the listing requests to establish a session
  // (its Set-Cookie response cookies are merged into the cookie jar).
  bootstrap_url: z.string().url().optional(),
  // Static headers; values may contain {{env.NAME}} placeholders (resolved server-side).
  headers: z.record(z.string(), z.string()).optional(),
  // Always-sent query params (the constant part of the listing URL).
  fixed_params: z.record(z.string(), z.string()).optional(),
});
export type StockSourceRequest = z.infer<typeof stockSourceRequestSchema>;

export const stockSourcePaginationSchema = z.object({
  // Query param carrying the page number.
  param: z.string().min(1),
  start: z.number().int().optional().default(1),
  // Records per page (informational; end-of-pagination is detected via an empty
  // or all-duplicates page, not a short one).
  page_size: z.number().int().positive(),
  // Hard cap on pages fetched per sync. Full-inventory syncs legitimately need
  // hundreds of pages — hitting the cap marks the run incomplete (no stale marking).
  max_pages: z.number().int().positive().max(2000).optional().default(1000),
  // Politeness delay between page fetches.
  delay_ms: z.number().int().min(0).max(30000).optional().default(1000),
});
export type StockSourcePagination = z.infer<typeof stockSourcePaginationSchema>;

// How a single captured value is post-processed. Superset of the tool-scraper
// transforms: adds price_int (entity-decode + digits) and miles_int ("16.1K
// miles" → 16100, "8,014 miles" → 8014).
export const stockSourceTransformSchema = z.enum([
  'text',
  'int',
  'float',
  'exists',
  'trim',
  'url_strip_query',
  'price_int',
  'miles_int',
]);
export type StockSourceTransform = z.infer<typeof stockSourceTransformSchema>;

export const stockSourceFieldSchema = z.object({
  // Regex run against each record chunk; capture group 1 is the value (ignored
  // for transform 'exists', where only match/no-match matters).
  regex: z.string().min(1),
  flags: z.string().optional(),
  transform: stockSourceTransformSchema.optional().default('text'),
  // Prepended to the final string value (e.g. a site origin for relative URLs).
  prefix: z.string().optional(),
  default: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
});
export type StockSourceField = z.infer<typeof stockSourceFieldSchema>;

export const stockSourceRecordSelectorSchema = z.object({
  // Regex whose group 1 is one record's HTML chunk (run with the 'g' flag).
  regex: z.string().min(1),
  flags: z.string().optional(),
  // Only keep chunks whose HTML contains this substring.
  must_contain: z.string().optional(),
});
export type StockSourceRecordSelector = z.infer<typeof stockSourceRecordSelectorSchema>;

export const stockSourceParseSchema = z.object({
  record_selector: stockSourceRecordSelectorSchema,
  fields: z.record(z.string(), stockSourceFieldSchema),
  // Field whose value uniquely identifies a record (cross-page dedupe + the
  // all-duplicates end-of-pagination stop).
  dedupe_by: z.string().optional(),
  // Drop a record if this field ends up empty/absent.
  require_field: z.string().optional(),
});
export type StockSourceParse = z.infer<typeof stockSourceParseSchema>;

// Canonical stock_items columns a parsed field may map onto.
export const stockCanonicalColumnSchema = z.enum([
  'external_id',
  'make',
  'model',
  'trim',
  'year',
  'price',
  'mileage',
  'body_type',
  'fuel_type',
  'transmission',
  'colour',
  'engine_size',
  'doors',
  'seats',
  'vehicle_type',
  'detail_url',
  'image_url',
]);
export type StockCanonicalColumn = z.infer<typeof stockCanonicalColumnSchema>;

// How the engine recognises that the stored session cookie has expired, so the
// source flips to needs_auth instead of wiping stock with a bogus empty crawl.
export const stockSourceAuthExpirySchema = z.object({
  // A redirect whose Location contains this substring means "not logged in".
  redirect_location_contains: z.string().optional().default('/login'),
  // Treat 0 records parsed on the FIRST page as an auth failure too (session
  // portals often 200-render a login page instead of redirecting).
  fail_on_empty_first_page: z.boolean().optional().default(true),
});
export type StockSourceAuthExpiry = z.infer<typeof stockSourceAuthExpirySchema>;

// Browser-based image enrichment for sites (e.g. Blazor Server) whose listing
// images render only client-side. A headless browser (env STOCK_SYNC_CDP_URL)
// walks the listing pages, waits for hydration, scrolls to trigger lazy loads,
// and maps each card's first image onto the matching stock_item.image_url.
export const stockImageEnrichmentSchema = z.object({
  // Enrichment only ever runs manually (POST /stock-sources/:id/enrich-images or
  // the UI button); this flag just marks the config as ready for it.
  enabled: z.boolean().optional().default(false),
  // Selector for one vehicle card element.
  card_selector: z.string().optional().default('.mud-card'),
  // Regex (group 1) extracting the record's external_id from a card link href.
  id_from_href_regex: z.string().min(1),
  // Selector for the image inside a card (first match wins).
  img_selector: z.string().optional().default('img'),
  // Ms to wait after navigation for the page to hydrate.
  settle_ms: z.number().int().min(0).max(60000).optional().default(5000),
  // Scroll step delay while sweeping the page (lazy-load trigger).
  scroll_delay_ms: z.number().int().min(0).max(5000).optional().default(600),
  // Hard cap on listing pages visited per enrichment run.
  max_pages: z.number().int().positive().max(2000).optional().default(1000),
  // Skip storing images whose URL matches this regex (dealer "no photo" placeholders).
  ignore_url_regex: z.string().optional(),
});
export type StockImageEnrichment = z.infer<typeof stockImageEnrichmentSchema>;

export const stockSourceConfigSchema = z.object({
  request: stockSourceRequestSchema,
  pagination: stockSourcePaginationSchema.optional(),
  parse: stockSourceParseSchema,
  // parsed-field name → canonical stock_items column. Unmapped parsed fields
  // still land in the item's `data` Json.
  field_map: z
    .record(z.string(), stockCanonicalColumnSchema)
    .refine((m) => Object.values(m).includes('external_id'), {
      message: 'field_map must map one parsed field to external_id',
    }),
  auth_expiry: stockSourceAuthExpirySchema.optional(),
  image_enrichment: stockImageEnrichmentSchema.optional(),
  // When the site gives one combined title (e.g. "Land Rover Discovery SE"),
  // list the makes to longest-prefix match against the parsed field mapped to
  // nothing; the sync service splits title → make/model using this list.
  make_list: z.array(z.string().min(1)).optional(),
  // Parsed field holding the combined title to split via make_list (default 'title').
  title_field: z.string().optional().default('title'),
  vehicle_type_default: z.string().optional(),
});
export type StockSourceConfig = z.infer<typeof stockSourceConfigSchema>;
